import { useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faStar as faStarSolid } from '@fortawesome/free-solid-svg-icons';
import { faStar as faStarRegular } from '@fortawesome/free-regular-svg-icons';
import type { VesselPosition, BaseVesselData, StaticVesselData } from '../types/vessel';
import { getVesselName, formatEta, getPositionAgeClass, timeSince, SPECIAL_VESSEL_NAMES } from '../utils/vessel';
import { translateDestination } from '../utils/destination';

interface VesselPopupProps {
  vessel: VesselPosition;
  vesselId: string;
  baseData?: BaseVesselData;
  staticData?: StaticVesselData;
  unlocodeRegister: Array<{ Country: string; Location: string; Name: string }>;
  countries: Array<{ CountryCode: string; CountryName: string }>;
  flagCode: string;
  isFavorite?: boolean;
  onToggleFavorite?: () => void;
}

export function VesselPopup({
  vessel,
  vesselId,
  baseData,
  staticData,
  unlocodeRegister,
  countries,
  flagCode,
  isFavorite,
  onToggleFavorite,
}: VesselPopupProps) {
  const [optimisticFavorite, setOptimisticFavorite] = useState(isFavorite ?? false);

  useEffect(() => {
    setOptimisticFavorite(isFavorite ?? false);
  }, [isFavorite]);

  const handleToggleFavorite = () => {
    setOptimisticFavorite(!optimisticFavorite);
    onToggleFavorite?.();
  };

  const name = getVesselName(vessel, vesselId, baseData, staticData);
  const { class: ageClass, status: ageStatus } = getPositionAgeClass(vessel.ReceivedDate);
  const destination = translateDestination(vessel.Destination, unlocodeRegister, countries);
  const eta = formatEta(vessel.ETA);
  const speedCourse = `${vessel.SoG?.toFixed(1) || vessel.Speed || '0.0'} kn / ${vessel.Heading || vessel.Course || 'N/A'}°`;
  const lastUpdated = vessel.ReceivedDate ? timeSince(new Date(vessel.ReceivedDate)) : 'N/A';
  const tankSize = baseData?.LNGtankCapacity && String(baseData.LNGtankCapacity).toUpperCase() !== 'NULL'
    ? `${baseData.LNGtankCapacity}m³`
    : '-';
  const length = staticData?.LengthOverallLOA ? `${staticData.LengthOverallLOA}m` : 'N/A';
  const width = staticData?.BreadthMoulded ? `${staticData.BreadthMoulded}m` : 'N/A';

  const isSpecialVessel = Object.keys(SPECIAL_VESSEL_NAMES).includes(vesselId);

  // Map Tailwind class names to actual colors
  const ageColor = ageClass.includes('green') ? '#22c55e' : ageClass.includes('yellow') ? '#eab308' : ageClass.includes('red') ? '#ef4444' : '#9ca3af';

  const fields = [
    { label: 'IMO', value: vessel.IMO || vesselId },
    { label: 'Ship Type', value: staticData?.ShipTypeLevel5 || baseData?.Type || 'N/A' },
    { label: 'LNG Tank Capacity', value: tankSize },
    { label: 'Owner', value: staticData?.RegisteredOwner || 'N/A' },
    { label: 'Operator', value: staticData?.Operator || 'N/A' },
    { label: 'Manager', value: staticData?.ShipManager || 'N/A' },
    { label: 'Gross Tonnage', value: staticData?.GrossTonnage || 'N/A' },
    { label: 'Dimensions', value: `${length} × ${width}` },
    { label: 'Built', value: baseData?.YearOfDelivery || 'N/A' },
    { label: 'Status', value: vessel.Status || 'N/A' },
    { label: 'Destination', value: destination },
    { label: 'ETA', value: eta },
    { label: 'Speed/Course', value: speedCourse },
    { label: 'Last Update', value: lastUpdated },
  ];

  return (
    <div>
      {/* Header */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '10px 12px',
        background: 'linear-gradient(135deg, #525390 0%, #6667A7 100%)',
        color: 'white',
      }}>
        <div
          style={{
            width: '10px',
            height: '10px',
            borderRadius: '50%',
            backgroundColor: ageColor,
            flexShrink: 0,
          }}
          title={ageStatus}
        />
        {flagCode && (
          <span 
            className={`fi fi-${flagCode}`} 
            style={{ fontSize: '16px', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', borderRadius: '2px' }} 
          />
        )}
        <span style={{ fontWeight: 600, fontSize: '14px', flex: 1 }}>{name}</span>
        {onToggleFavorite && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleToggleFavorite();
            }}
            style={{
              background: 'none',
              border: 'none',
              outline: 'none',
              cursor: 'pointer',
              color: optimisticFavorite ? '#f59e0b' : 'rgba(255,255,255,0.7)',
              padding: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
            title={optimisticFavorite ? 'Remove from favorites' : 'Add to favorites'}
          >
            <FontAwesomeIcon icon={optimisticFavorite ? faStarSolid : faStarRegular} style={{ fontSize: '16px' }} />
          </button>
        )}
      </div>

      {/* Content */}
      <div style={{ padding: '10px 12px', maxHeight: '250px', overflowY: 'auto' }}>
        {isSpecialVessel && (
          <div style={{
            marginBottom: '8px',
            padding: '8px',
            background: '#fef3c7',
            border: '1px solid #fcd34d',
            borderRadius: '4px',
            fontSize: '11px',
            color: '#92400e',
          }}>
            Position for this vessel is approximate and based on AIS signal from the pusher tug
          </div>
        )}
        {fields.map((field) => (
          <div key={field.label} style={{
            display: 'flex',
            padding: '6px 0',
            borderBottom: '1px solid rgba(82,83,144,0.07)',
          }}>
            <span style={{
              fontSize: '9px',
              textTransform: 'uppercase',
              color: 'rgba(82,83,144,0.9)',
              fontWeight: 600,
              width: '40%',
              flexShrink: 0,
            }}>{field.label}</span>
            <span style={{
              fontSize: '11px',
              fontWeight: 500,
              color: '#333',
            }}>{field.value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
